'use client'

import { Radio } from '@base-ui/react/radio'
import { RadioGroup } from '@base-ui/react/radio-group'
import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithRef, ReactNode } from 'react'

import { breakpoints, colors, controlSizes, fonts, fontSizes, radii, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  group: { display: 'inline-flex', flexWrap: 'wrap', gap: spacing.space4, borderRadius: radii.small },
  option: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    paddingInline: spacing.space12,
    borderRadius: radii.small,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.borderStrong,
    backgroundColor: { default: colors.background, ':is([data-checked])': colors.foreground },
    color: { default: colors.foreground, ':is([data-checked])': colors.inverse },
    fontFamily: fonts.interface,
    fontSize: fontSizes.small,
    cursor: { default: 'pointer', ':is([data-disabled])': 'not-allowed' },
    opacity: { default: 1, ':is([data-disabled])': 0.5 },
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: 3,
  },
  compact: {
    minHeight: { default: controlSizes.compact, [breakpoints.coarse]: controlSizes.touch },
    paddingInline: spacing.space8,
    fontFamily: fonts.sans,
    borderColor: colors.border,
  },
  muted: {
    color: { default: colors.muted, ':is([data-checked])': colors.inverse },
    backgroundColor: { default: colors.background, ':is([data-checked])': colors.muted },
  },
  icon: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
})

export type SegmentedControlItem<Value extends string> = {
  value: Value
  label: string
  icon?: ReactNode
  muted?: boolean
}

type SegmentedControlOptions<Value extends string> = {
  value: Value
  items: readonly SegmentedControlItem<Value>[]
  size?: 'sm' | 'md'
  display?: 'label' | 'icon'
  disabled?: boolean
  name?: string
  form?: string
  required?: boolean
  readOnly?: boolean
  onValueChange(value: Value): void
  'aria-label': string
}

export type SegmentedControlProps<Value extends string> = Omit<
  ComponentPropsWithRef<'div'>,
  'className' | 'style' | 'children' | 'defaultValue' | 'onChange'
> &
  SegmentedControlOptions<Value>

/**
 * Choose one value from a short, always-visible set. Use Select for longer lists and ToggleButton for
 * independent pressed states. Applications supply the selected value and handle changes. Icon display uses each
 * item's label for its accessible name and tooltip; omit icons when text communicates the choice better.
 *
 * Status: supported.
 */
export function SegmentedControl<Value extends string>({
  items,
  size = 'md',
  display = 'label',
  value,
  disabled,
  onValueChange,
  ...props
}: SegmentedControlProps<Value>) {
  return (
    <RadioGroup<Value>
      {...props}
      value={value}
      disabled={disabled}
      onValueChange={(next) => onValueChange(next)}
      {...stylex.props(styles.group)}
    >
      {items.map((item) => (
        <Radio.Root
          key={item.value}
          value={item.value}
          aria-label={item.label}
          title={display === 'icon' ? item.label : undefined}
          {...stylex.props(styles.option, size === 'sm' && styles.compact, item.muted && styles.muted)}
        >
          {display === 'icon' && item.icon ? (
            <span aria-hidden="true" {...stylex.props(styles.icon)}>
              {item.icon}
            </span>
          ) : (
            item.label
          )}
        </Radio.Root>
      ))}
    </RadioGroup>
  )
}
